import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function DriverEarnings() {
  const { api } = useAuth();
  const { formatPrice } = useCurrency();
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const response = await api.get('/drivers/earnings');
      setEarnings(response.data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Earnings</h1>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/2 mb-4"></div>
            <div className="h-10 bg-slate-200 rounded"></div>
          </div>
          <div className="bg-white rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/2 mb-4"></div>
            <div className="h-10 bg-slate-200 rounded"></div>
          </div>
          <div className="bg-white rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/2 mb-4"></div>
            <div className="h-10 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const totalEarnings = earnings?.total_earnings || 0;
  const totalDeliveries = earnings?.total_deliveries || 0;
  const avgPerDelivery = totalDeliveries > 0 ? (totalEarnings / totalDeliveries) : 0;

  const renderDeliveryHistory = () => {
    if (!earnings?.deliveries || earnings.deliveries.length === 0) {
      return <p className="text-center text-slate-500 py-8">No delivery history yet</p>;
    }

    const items = [];
    for (let i = 0; i < earnings.deliveries.length; i++) {
      const delivery = earnings.deliveries[i];
      items.push(
        <div 
          key={i} 
          className="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
        >
          <div>
            <p className="font-medium text-slate-900">Delivery</p>
            <p className="text-sm text-slate-500">{formatDate(delivery.created_at)}</p>
          </div>
          <span className="font-bold text-green-500">+{formatPrice(delivery.delivery_fee)}</span>
        </div>
      );
    }
    return <div className="space-y-3">{items}</div>;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Earnings</h1>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Earnings</p>
                <p className="text-3xl font-bold text-slate-900">{formatPrice(totalEarnings)}</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-green-50 flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Deliveries</p>
                <p className="text-3xl font-bold text-slate-900">{totalDeliveries}</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Per Delivery</p>
                <p className="text-3xl font-bold text-slate-900">{formatPrice(avgPerDelivery)}</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-orange-50 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Delivery History</CardTitle>
        </CardHeader>
        <CardContent>
          {renderDeliveryHistory()}
        </CardContent>
      </Card>
    </div>
  );
}
